import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { AnimatePresence, motion } from "framer-motion";
import "../SchoolSignup.css";

const API_BASE = "https://dgps.co.in/api/schoolsignup";

// Step transition variants (slide left/right)
const stepVariants = {
    initial: (direction) => ({
        opacity: 0,
        x: direction > 0 ? 40 : -40,
        scale: 0.98,
    }),
    animate: {
        opacity: 1,
        x: 0,
        scale: 1,
    },
    exit: (direction) => ({
        opacity: 0,
        x: direction > 0 ? -40 : 40,
        scale: 0.98,
    }),
};

export default function SchoolSignup() {
    const [step, setStep] = useState(1);
    const [direction, setDirection] = useState(0); // step animation direction
    const [loading, setLoading] = useState(false);

    const [plans, setPlans] = useState([]);
    const [tempId, setTempId] = useState(localStorage.getItem("temp_id") || null);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedPlanData, setSelectedPlanData] = useState(null);

    const [preview, setPreview] = useState(null);

    const [formData, setFormData] = useState(
        JSON.parse(localStorage.getItem("school_form")) || {
            school_name: "",
            school_code: "",
            address: "",
            mobile: "",
            email: "",
            domain: "",
            logo: null,
        }
    );

    // -------- helper: animated step change ----------
    function goToStep(nextStep) {
        setDirection(nextStep > step ? 1 : -1);
        setStep(nextStep);
    }

    function formatMoney(amount) {
        return Number(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    // Toast handler
    const toast = (icon, title) => {
        Swal.fire({
            toast: true,
            icon,
            title,
            position: "top-right",
            timer: 2200,
            showConfirmButton: false,
            customClass: { container: "ssp-toast" },
        });
    };

    // 🔥 PAYMENT REDIRECT HANDLER (Runs FIRST on page load)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("payment");

        if (status === "success") {
            Swal.fire({
                icon: "success",
                title: "Payment Successful",
                text: "Registration completed successfully",
                timer: 2500,
                showConfirmButton: false,
            });

            // 🧹 CLEAR ALL LOCAL STORAGE (signup related)
            localStorage.removeItem("temp_id");
            localStorage.removeItem("school_form");
            localStorage.removeItem("selected_plan");

            // Clean the URL (remove ?payment=success)
            window.history.replaceState({}, "", window.location.pathname);

            // Redirect to login
            setTimeout(() => (window.location.href = "/login"), 2500);
            return; // 🔥 STOP everything else
        }

        if (status === "failed") {
            Swal.fire({
                icon: "error",
                title: "Payment Failed",
                text: "Please try again",
                showConfirmButton: true,
            });

            // Clean URL
            window.history.replaceState({}, "", window.location.pathname);

            goToStep(3); // Retry payment
            return; // 🔥 STOP resume logic
        }
    }, []);

    // 🔥 RESUME LOGIC — only runs if NO payment callback exists
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const hasPaymentParam = params.get("payment");

        // ❌ If payment success/failed is in URL → stop resume
        if (hasPaymentParam) return;

        if (!tempId) return;

        axios
            .post(`${API_BASE}/resume`, { temp_id: tempId })
            .then((res) => {
                if (!res.data.success) return;

                const { step_status, selected_plan_id } = res.data.data;

                if (step_status === "initiated") {
                    goToStep(1);
                    fetchPlans();
                }
                else if (step_status === "plan_selected" || step_status === "pending") {
                    setSelectedPlan(selected_plan_id);
                    fetchPlans();
                    goToStep(3);
                }
                else if (step_status === "paid") {
                    window.location.href = "/login";
                }
            })
            .catch(() => { });
    }, []);


    // Fetch plans
    const fetchPlans = async () => {
        try {
            const res = await axios.get(`${API_BASE}/pricing_plans`);
            if (res.data.success) setPlans(res.data.data);
        } catch {
            toast("error", "Plan fetch failed");
        }
    };




    // Auto Step 2 if resumed
    useEffect(() => {
        if (tempId) {
            fetchPlans();
        }
    }, [tempId]);

    // Base64 Converter
    const toBase64 = (file) =>
        new Promise((resolve) => {
            const r = new FileReader();
            r.readAsDataURL(file);
            r.onload = () => resolve(r.result);
        });

    // File upload
    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return toast("error", "Invalid file");

        setFormData({ ...formData, logo: file });

        const r = new FileReader();
        r.onload = () => setPreview(r.result);
        r.readAsDataURL(file);
    };

    // Load previous preview
    useEffect(() => {
        const saved = JSON.parse(localStorage.getItem("school_form"));
        if (saved?.logo && typeof saved.logo === "string") {
            setPreview(saved.logo);
        }
    }, []);

    // STEP 1 validation
    const validateStep = () => {
        if (!/^[A-Z\s-]+$/.test(formData.school_name))
            return toast("error", "Invalid school name");

        if (!/^[A-Z0-9]+$/.test(formData.school_code))
            return toast("error", "Invalid school code");

        if (!/^[0-9]{10}$/.test(formData.mobile))
            return toast("error", "Invalid mobile");

        return true;
    };

    // STEP 1 submission
    const handleStepOne = async (e) => {
        e.preventDefault();

        if (!validateStep()) return;

        setLoading(true);

        let logoBase64 = null;
        if (formData.logo) logoBase64 = await toBase64(formData.logo);

        const payload = { ...formData, logo: logoBase64, temp_id: tempId };

        try {
            let res;

            if (tempId) {
                res = await axios.post(`${API_BASE}/update_school_info`, payload);
            } else {
                res = await axios.post(`${API_BASE}/sign_up`, payload);
            }

            if (res.data.success) {
                toast("success", "Step 1 Completed");

                if (!tempId) {
                    const newId = res.data.data.temp_id;
                    setTempId(newId);
                    localStorage.setItem("temp_id", newId);
                }

                localStorage.setItem("school_form", JSON.stringify(formData));
                fetchPlans();
                goToStep(2);
            } else {
                toast("error", res.data.message);
            }
        } catch {
            toast("error", "Submit failed");
        }

        setLoading(false);
    };

    // STEP 2 select plan
    const handleSelectPlan = async (planId) => {
        const temp_id = localStorage.getItem("temp_id");
        if (!temp_id) return toast("error", "temp_id missing");

        const selected = plans.find((p) => p.id === planId);
        setSelectedPlan(planId);
        setSelectedPlanData(selected);

        try {
            const res = await axios.post(`${API_BASE}/select_plan`, {
                temp_id,
                plan_id: planId,
            });

            if (res.data.success) {
                toast("success", "Plan selected");
                goToStep(3);
            }
        } catch {
            toast("error", "Plan selection failed");
        }
    };

    // STEP 3 Payment
    const handlePayment = async () => {
        const temp_id = localStorage.getItem("temp_id");
        if (!temp_id) return;

        try {
            setLoading(true);

            const res = await axios.post(`${API_BASE}/pay/${temp_id}`);

            if (res.data.success && res.data.data.payment_url) {
                window.location.href = res.data.data.payment_url;
            } else {
                toast("error", "Payment initiation failed");
            }
        } catch {
            toast("error", "Payment failed");
        }

        setLoading(false);
    };

    // Progress Bar component
    const ProgressBar = () => (
        <div className="ssp-progress-bar">
            <div
                className="ssp-progress-fill"
                style={{ width: step === 1 ? "33%" : step === 2 ? "66%" : "100%" }}
            />
            <div className="ssp-progress-steps">
                <span className={step >= 1 ? "active" : ""}>1</span>
                <span className={step >= 2 ? "active" : ""}>2</span>
                <span className={step === 3 ? "active" : ""}>3</span>
            </div>
        </div>
    );

    const StepNav = () =>
        step > 1 && (
            <button className="ssp-back-btn" onClick={() => goToStep(step - 1)}>
                ← Previous
            </button>
        );

    // Step content (without animation wrapper)
    const renderStepContent = () => {
        if (step === 1) {
            return (
                <form onSubmit={handleStepOne} className="ssp-card">
                    <h2>Step 1: School Information</h2>

                    <div className="ssp-form-grid">
                        <input
                            type="text"
                            required
                            placeholder="School Name"
                            value={formData.school_name}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    school_name: e.target.value.toUpperCase(),
                                })
                            }
                        />

                        <input
                            type="text"
                            required
                            placeholder="School Short Code"
                            value={formData.school_code}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    school_code: e.target.value.toUpperCase(),
                                })
                            }
                        />

                        <textarea
                            required
                            placeholder="Full Address"
                            value={formData.address}
                            onChange={(e) =>
                                setFormData({ ...formData, address: e.target.value })
                            }
                        />

                        <input
                            type="text"
                            required
                            placeholder="Mobile"
                            maxLength="10"
                            value={formData.mobile}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    mobile: e.target.value.replace(/\D/g, ""),
                                })
                            }
                        />

                        <input
                            type="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={(e) =>
                                setFormData({ ...formData, email: e.target.value })
                            }
                        />

                        <input
                            type="text"
                            placeholder="Domain"
                            value={formData.domain}
                            onChange={(e) =>
                                setFormData({ ...formData, domain: e.target.value })
                            }
                        />

                        <div className="ssp-logo-upload">
                            <label>
                                School Logo (PNG)
                                <input type="file" accept="image/png" onChange={handleFile} />
                            </label>
                            {preview && (
                                <div className="ssp-logo-preview">
                                    <img src={preview} alt="" />
                                </div>
                            )}
                        </div>
                    </div>

                    <button type="submit" disabled={loading}>
                        {loading ? "Submitting..." : "Save & Continue →"}
                    </button>
                </form>
            );
        }

        if (step === 2) {
            return (
                <div className="ssp-card">
                    <h2>Step 2: Choose Your Plan</h2>

                    <div className="ssp-plan-grid">
                        {plans.map((p) => (
                            <div
                                key={p.id}
                                className={`ssp-plan-card ${selectedPlan === p.id ? "selected" : ""
                                    }`}
                                onClick={() => handleSelectPlan(p.id)}
                            >
                                <h3>{p.name}</h3>

                                <div className="plan-details">
                                    <div className="plan-line">
                                        <span>Setup Cost</span>
                                        <b>₹{formatMoney(p.setup_cost)}</b>
                                    </div>

                                    <div className="plan-line">
                                        <span>Monthly Fee</span>
                                        <b>
                                            ₹{formatMoney(p.monthly_fee)} × 12 = ₹
                                            {formatMoney(p.monthly_fee * 12)}
                                        </b>
                                    </div>
                                    <div className="plan-line discount">
                                        <span>Discount</span>

                                        <div className="discount-amount">
                                            <span className="disc-icon"></span>
                                            <span className="disc-value">- ₹{formatMoney(p.discount)}</span>
                                        </div>
                                    </div>

                                    <div className="plan-line total">
                                        <span>Total</span>
                                        <b>₹{formatMoney(p.total_amount)}</b>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <StepNav />
                </div>
            );
        }

        if (step === 3) {
            return (
                <div className="ssp-card">
                    <h2>Step 3: Confirm & Pay</h2>

                    <div className="ssp-summary-box">
                        <h3>{selectedPlanData?.name}</h3>
                        <p>
                            <strong>Total Payable: </strong>₹
                            {selectedPlanData?.total_amount}
                        </p>
                        <p style={{ marginTop: 4, fontSize: 14, color: "#6b7280" }}>
                            (Includes setup, yearly & monthly costs)
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                        <button onClick={handlePayment} disabled={loading}>
                            {loading ? "Processing..." : "Pay Now"}
                        </button>
                        <StepNav />
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="school-signup-page">
            <div className="ssp-box">
                <div className="ssp-header">
                    <h1>School Registration Portal</h1>
                    <p>Complete your registration in 3 easy steps</p>
                </div>

                <ProgressBar />

                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={step}
                        variants={stepVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        custom={direction}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                    >
                        {renderStepContent()}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
}
